"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Hidden = exports.useHidden = void 0;
const tslib_1 = require("tslib");
const core_1 = require("@emotion/core");
const styled_1 = tslib_1.__importDefault(require("@emotion/styled"));
const react_core_1 = require("@neo-one/react-core");
const React = tslib_1.__importStar(require("react"));
const styled_tools_1 = require("styled-tools");
const transition_1 = require("./transition");
const { forwardRef, useCallback, useEffect, useRef, useState, useReducer } = React;
const hiddenReducer = (state, action) => {
    switch (action.type) {
        case 'show':
            return { visible: true };
        case 'hide':
            return { visible: false };
        case 'toggle':
            return { visible: !state.visible };
        default:
            throw new Error();
    }
};
exports.useHidden = (propVisible = false) => {
    const [state, dispatch] = useReducer(hiddenReducer, { visible: propVisible });
    return Object.assign(Object.assign({}, state), { show: () => dispatch({ type: 'show' }), hide: () => dispatch({ type: 'hide' }), toggle: () => dispatch({ type: 'toggle' }) });
};
const HiddenComponent = forwardRef((_a, refIn) => {
    var { visible: propVisible = false, transitioning: propTransitioning = false, unmount = false, hideOnEsc = false, hideOnClickOutside = false, hide } = _a, props = tslib_1.__rest(_a, ["visible", "transitioning", "unmount", "hideOnEsc", "hideOnClickOutside", "hide"]);
    const myRef = useRef(null);
    const ref = refIn === null ? myRef : refIn;
    const [visible, setVisible] = useState(!!propVisible);
    const [transitioning, setTransitioning] = useState(!!propTransitioning);
    const refVisible = useRef(visible);
    refVisible.current = visible;
    const handleTransitionEnd = useCallback(() => {
        if (unmount && !propVisible) {
            setTransitioning(false);
        }
    }, [unmount, propVisible]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && propVisible && hide) {
                hide();
            }
        };
        const handleClickOutside = (e) => {
            const node = ref.current;
            if (node !== null && !node.contains(e.target) && propVisible && hide !== undefined) {
                setTimeout(() => {
                    if (refVisible.current) {
                        hide();
                    }
                });
            }
        };
        if (hideOnEsc) {
            document.body.addEventListener('keydown', handleKeyDown);
        }
        if (hideOnClickOutside) {
            document.body.addEventListener('click', handleClickOutside);
        }
        return () => {
            document.body.removeEventListener('keydown', handleKeyDown);
            document.body.removeEventListener('click', handleClickOutside);
        };
    }, [propVisible, hide, ref, refVisible, hideOnEsc, hideOnClickOutside]);
    useEffect(() => {
        if (typeof window !== 'undefined' && unmount && transition_1.hasTransition(props)) {
            if (propVisible) {
                setTransitioning(true);
                requestAnimationFrame(() => {
                    setTransitioning(false);
                    setVisible(true);
                });
            }
            else {
                setTransitioning(true);
                setVisible(false);
            }
        }
        else {
            setVisible(propVisible);
        }
    }, [propVisible, setTransitioning, setVisible, ...transition_1.getTransitionArray(props)]);
    if (unmount && !visible && !transitioning) {
        return null;
    }
    const rest = transition_1.excludeTransition(props);
    return (React.createElement(react_core_1.Box, Object.assign({}, rest, { ref: ref, "aria-hidden": !visible, hidden: !visible && !transition_1.hasTransition(props), onTransitionEnd: react_core_1.callAll(handleTransitionEnd, rest.onTransitionEnd) })));
});
const hiddenTheme = styled_tools_1.theme('Hidden');
const hiddenTransformOrigin = (props) => {
    const transformOrigin = transition_1.originWithProps(props);
    if (transition_1.hasTransition(props)) {
        return core_1.css `
      transform-origin: ${transformOrigin(props)};
      transition: all ${props.duration} ${props.timing} ${props.delay};
    `;
    }
    return '';
};
const hiddenTransition = (props) => {
    const slideWith = transition_1.slideWithProps(props);
    const scaleWith = transition_1.scaleWithProps(props);
    if (transition_1.hasTransition(props)) {
        return core_1.css `
      transform: ${slideWith(props)} ${scaleWith};
      visibility: hidden;
      will-change: transform, opacity;
      display: none !important;
    `;
    }
    return core_1.css `
    display: none !important;
  `;
};
exports.Hidden = styled_1.default(HiddenComponent) `
  transform: ${transition_1.translateWithProps};
  ${hiddenTransformOrigin};

  &[aria-hidden='true'] {
    pointer-events: none;
    ${styled_tools_1.ifProp('fade', 'opacity: 0')};
    ${hiddenTransition};
  }

  ${hiddenTheme};
`;
exports.Hidden.defaultProps = {
    duration: '250ms',
    timing: 'ease-in-out',
};

//# sourceMappingURL=Hidden.js.map
